/**
 * Payroll Payslip Template Renderer
 */

import { renderTemplate } from "../templateRenderer";
import {
  PAYROLL_PAYSLIP_TEMPLATE,
  PAYROLL_PAYSLIP_CSS,
} from "./loader";

export interface PayrollPayslipContext {
  company: {
    name: string;
    address?: string;
    phone?: string;
    logo_url?: string;
  };
  payroll: {
    payroll_month: string;
    employee_name: string;
    designation?: string;
    nic?: string;
    epf_etf_no?: string;
    salary_type: string;
    present_days: number;
    absent_days: number;
    basic_salary: number;
    allocated_attendance_allowance?: number;
    attendance_allowance_paid?: number;
    fuel_transport?: number;
    production_incentive?: number;
    ot_hours?: number;
    ot_rate_hour?: number;
    ot_amount?: number;
    allowances?: number;
    other_allowances?: number;
    total_allowances?: number;
    nopay_days?: number;
    nopay_deduction?: number;
    employee_epf?: number;
    salary_advance?: number;
    deductions?: number;
    other_deductions?: number;
    net_basic?: number;
    net_allowances?: number;
    net_payable_basic?: number;
    net_payable_allowances?: number;
    net_salary: number;
    employer_epf?: number;
    employer_etf?: number;
    company_govt_allocation?: number;
    status: string;
    paid_at?: string;
    payment_method?: string;
  };
  isForPrint?: boolean;
}

const BASE_TEMPLATE = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <title>{{ title }}</title>
  <style>{{ styles }}</style>
</head>
<body>
  {{ content | safe }}
</body>
</html>`;

function formatNum(val?: number): string {
  const num = Number(val) || 0;
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function renderPayrollPayslip(ctx: PayrollPayslipContext): string {
  const { company, payroll, isForPrint = false } = ctx;

  const basicSalary = Number(payroll.basic_salary) || 0;
  const attendanceAllowancePaid = Number(payroll.attendance_allowance_paid) || 0;
  const allocatedAttendanceAllowance = Number(payroll.allocated_attendance_allowance) || 0;
  const fuelTransport = Number(payroll.fuel_transport) || 0;
  const productionIncentive = Number(payroll.production_incentive) || 0;
  const otAmount = Number(payroll.ot_amount) || 0;
  const otherAllowances = Number(payroll.other_allowances ?? payroll.allowances) || 0;
  const totalAllowances = Number(payroll.total_allowances ?? (attendanceAllowancePaid + fuelTransport + productionIncentive + otAmount + otherAllowances)) || 0;

  const grossEarnings = basicSalary + totalAllowances;

  const nopayDeduction = Number(payroll.nopay_deduction) || 0;
  const employeeEpf = Number(payroll.employee_epf) || 0;
  const salaryAdvance = Number(payroll.salary_advance) || 0;
  const otherDeductions = Number(payroll.other_deductions ?? payroll.deductions) || 0;
  const totalDeductions = nopayDeduction + employeeEpf + salaryAdvance + otherDeductions;

  const netBasic = Number(payroll.net_basic) || (basicSalary - nopayDeduction);
  const netAllowances = Number(payroll.net_allowances) || (totalAllowances - salaryAdvance);
  const netPayableBasic = Number(payroll.net_payable_basic) || (netBasic - employeeEpf);
  const netPayableAllowances = Number(payroll.net_payable_allowances) || netAllowances;
  const netSalary = Number(payroll.net_salary) || (netPayableBasic + netPayableAllowances);

  const employerEpf = Number(payroll.employer_epf) || 0;
  const employerEtf = Number(payroll.employer_etf) || 0;
  const companyGovtAllocation = Number(payroll.company_govt_allocation) || (employerEpf + employerEtf);

  const contentContext = {
    companyName: company.name,
    companyAddress: company.address || "",
    companyPhone: company.phone || "",
    logoUrl: company.logo_url || "",

    payrollMonth: payroll.payroll_month,
    employeeName: payroll.employee_name,
    designation: payroll.designation || "Staff",
    nic: payroll.nic || "—",
    epfEtfNo: payroll.epf_etf_no || "—",
    salaryType: payroll.salary_type || "Monthly",
    presentDays: payroll.present_days || 0,
    absentDays: payroll.absent_days || 0,
    status: payroll.status || "Draft",
    paidAt: payroll.paid_at || "",
    paymentMethod: payroll.payment_method || "—",
    generatedAt: new Date().toLocaleString(),

    basicSalary: formatNum(basicSalary),
    allocatedAttendanceAllowance: formatNum(allocatedAttendanceAllowance),
    attendanceAllowancePaid: formatNum(attendanceAllowancePaid),
    fuelTransport: formatNum(fuelTransport),
    productionIncentive: formatNum(productionIncentive),
    otHours: payroll.ot_hours || 0,
    otRateHour: formatNum(payroll.ot_rate_hour),
    otAmount: formatNum(otAmount),
    otherAllowances: formatNum(otherAllowances),
    totalAllowances: formatNum(totalAllowances),

    grossEarnings: formatNum(grossEarnings),
    totalDeductions: formatNum(totalDeductions),

    nopayDays: payroll.nopay_days || 0,
    nopayDeduction: formatNum(nopayDeduction),
    employeeEpf: formatNum(employeeEpf),
    salaryAdvance: formatNum(salaryAdvance),
    otherDeductions: formatNum(otherDeductions),

    netBasic: formatNum(netBasic),
    netAllowances: formatNum(netAllowances),
    netPayableBasic: formatNum(netPayableBasic),
    netPayableAllowances: formatNum(netPayableAllowances),
    netSalary: formatNum(netSalary),

    employerEpf: formatNum(employerEpf),
    employerEtf: formatNum(employerEtf),
    companyGovtAllocation: formatNum(companyGovtAllocation),
  };

  const cssVars = `
    :root {
      --ps-bg: ${isForPrint ? "#ffffff" : "#f8fafc"};
      --ps-radius: ${isForPrint ? "0" : "8px"};
      --ps-shadow: ${isForPrint ? "none" : "0 4px 20px rgba(0,0,0,0.08)"};
      --ps-border: ${isForPrint ? "1px solid #e2e8f0" : "1px solid #cbd5e1"};
    }
  `;

  const content = renderTemplate(PAYROLL_PAYSLIP_TEMPLATE, contentContext);
  const styles = cssVars + "\n" + PAYROLL_PAYSLIP_CSS;

  return renderTemplate(BASE_TEMPLATE, {
    title: `Payslip - ${payroll.employee_name} (${payroll.payroll_month})`,
    styles,
    content,
  });
}
